import { useId, useState, type ReactNode } from 'react';
import { Check, ChevronDown, Download, Eye, Gift, FileText } from 'lucide-react';
import { GiftPhotoVariant, type GiftCardDraft } from '@diana/contracts';
import { Button } from '../../ui/index.js';
import './GiftCardSection.css';
import { GiftCardArtwork } from './GiftCardArtwork.js';
export { GiftCardArtwork } from './GiftCardArtwork.js';

export function GiftCardEmptyState({ onGenerate }: { onGenerate(): void }) {
  return (
    <div className="gift-card-empty">
      <span className="gift-card-empty__icon" aria-hidden="true">
        <Gift size={24} strokeWidth={1.5} />
      </span>
      <div className="gift-card-empty__content">
        <strong>No gift card yet</strong>
        <p>Choose a photo and add a recipient to prepare the PDF.</p>
        <Button variant="outline" leadingIcon={FileText} onClick={onGenerate}>
          Generate card
        </Button>
      </div>
    </div>
  );
}

export function GiftCardSummary({
  recipient,
  experience,
  validUntil,
  photo,
  onEdit,
  downloadUrl,
}: {
  recipient: string;
  experience?: string;
  validUntil: string;
  photo?: string;
  onEdit(): void;
  downloadUrl?: string;
}) {
  return (
    <div className="gift-card-document">
      <button
        type="button"
        className="gift-card-document__edit"
        onClick={onEdit}
        aria-label={`Edit gift card for ${recipient}`}
      >
        <span className="gift-card-document__miniature" aria-hidden="true">
          <span>Gift card</span>
          {photo ? (
            <img
              src={photo}
              alt=""
              onError={(event) => {
                event.currentTarget.style.visibility = 'hidden';
              }}
            />
          ) : null}
          <small>For {recipient}</small>
        </span>
        <span className="gift-card-document__details">
          <strong>Gift card for {recipient}</strong>
          {experience ? <span>{experience}</span> : null}
          <span>Valid until {validUntil}</span>
        </span>
      </button>
      {downloadUrl ? (
        <a
          className="shoot-button shoot-button--outline gift-card-document__download"
          href={downloadUrl}
        >
          <Download size={16} aria-hidden="true" />
          Download card
        </a>
      ) : null}
    </div>
  );
}

export function GiftPhotoPicker({
  value,
  custom,
  disabled,
  photoSrc,
  onChange,
}: {
  value: GiftCardDraft['photoVariant'];
  custom: boolean;
  disabled?: boolean;
  photoSrc(variant: GiftCardDraft['photoVariant']): string;
  onChange(variant: GiftCardDraft['photoVariant']): void;
}) {
  const name = useId();
  return (
    <fieldset className="gift-photo-picker" disabled={disabled}>
      <legend className="gift-card-panel__group-title">Photograph</legend>
      <div className="gift-photo-picker__options">
        {GiftPhotoVariant.options.map((variant) => (
          <label key={variant} className="gift-photo-option">
            <input
              type="radio"
              name={name}
              value={variant}
              checked={!custom && value === variant}
              onChange={() => onChange(variant)}
            />
            <span className="gift-photo-option__image">
              <img src={photoSrc(variant)} alt="" />
              {!custom && value === variant ? (
                <span className="gift-photo-option__check">
                  <Check size={13} aria-hidden="true" />
                </span>
              ) : null}
            </span>
            <span>{variant}</span>
          </label>
        ))}
      </div>
    </fieldset>
  );
}

export function GiftCardPreview({
  draft,
  shootId,
  children,
}: {
  draft: GiftCardDraft;
  shootId?: string;
  children?: ReactNode;
}) {
  const [expanded, setExpanded] = useState(false);
  const id = useId();
  return (
    <aside className="gift-card-panel__review" aria-label="Gift card preview and PDF">
      <Button
        className="gift-card-preview-toggle"
        variant="outline"
        leadingIcon={Eye}
        trailingIcon={ChevronDown}
        aria-expanded={expanded}
        aria-controls={id}
        onClick={() => setExpanded((current) => !current)}
      >
        {expanded ? 'Hide preview' : 'Preview'}
      </Button>
      <div id={id} className="gift-card-preview-content" data-expanded={expanded}>
        <div className="gift-card-panel__preview-label">Layout preview</div>
        <GiftCardArtwork draft={draft} shootId={shootId} />
      </div>
      {children}
    </aside>
  );
}
